import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { PlayIcon } from '@shared/ui';
import type { ActiveSessionSummary } from '@shared/types';
import './hero.css';

interface ActiveSessionHeroProps {
  /** Активная незавершённая сессия пользователя (из /sessions/active). */
  session: ActiveSessionSummary;
  onContinue: () => void;
  /** Ссылка «Выбрать другую тренировку» под кнопкой продолжения. */
  onChooseOther?: () => void;
  /** Раскладка телефона: кнопка и ссылка — под текстом, во всю ширину. */
  phone?: boolean;
}

/**
 * Продолжение последней тренировки — верх карточки «Тренировка на сегодня» (рамку и строку серии даёт `TodayCard`,
 * новая главная 2026-10-22; ТЗ главной § 11):
 * «Тренировка не закончена», «Осталось 6 карточек · 2 к повторению», полоса прогресса.
 * Состав и прогресс восстановлены с сервера; is_ready_to_boss открывает финал, в том числе после
 * досрочного завершения.
 */
export function ActiveSessionHero({ session, onContinue, onChooseOther, phone = false }: ActiveSessionHeroProps) {
  const { t } = useTranslation();
  const isBoss = session.status === 'is_ready_to_boss';

  const done = session.done; // верно отвечено (DONE)
  const redo = session.toRedo; // промах, ждёт пересдачи (TO_REPEAT)
  const left = Math.max(0, session.remaining - session.toRedo); // ещё не показаны
  const remaining = redo + left; // = session.remaining
  const total = done + redo + left;
  const share = (n: number) => (total > 0 ? `${(n / total) * 100}%` : '0%');

  const title = isBoss
    ? t('dashboard.active.bossReady')
    : t('dashboard.active.remaining', { count: remaining }) +
      (redo > 0 ? ` · ${t('dashboard.active.redoInline', { count: redo })}` : '');

  const body = (
    <>
      <div className="text-[12px] font-semibold uppercase leading-4 tracking-[0.14em] text-muted">
        {t('dashboard.active.kicker')}
      </div>
      <h2
        className={cn(
          'mt-2 line-clamp-2 font-serif font-medium tracking-[-0.01em] text-fg',
          phone ? 'text-[22px] leading-[28px]' : 'text-[28px] leading-[34px]',
        )}
      >
        {title}
      </h2>
      {isBoss ? (
        <p className="mt-2 text-[14px] leading-5 text-muted">{t('dashboard.active.bossSub')}</p>
      ) : (
        // Пройдено — светлым, к повторению — красным, впереди — фон полосы.
        <div aria-hidden className="mt-3 flex h-1 w-full max-w-[480px] overflow-hidden rounded-pill bg-line">
          <span className="h-full bg-fg" style={{ width: share(done) }} />
          <span className="h-full bg-danger" style={{ width: share(redo) }} />
        </div>
      )}
    </>
  );

  const actions = (
    <>
      <button
        type="button"
        onClick={onContinue}
        className={cn(
          'flex flex-none items-center justify-center gap-2.5 whitespace-nowrap rounded-pill bg-accent font-semibold text-fg-inv transition-colors hover:bg-accent-hi',
          phone ? 'h-12 w-full text-[16px]' : 'h-[52px] px-7 text-[16px]',
        )}
      >
        <PlayIcon className="h-3.5 w-3.5" aria-hidden />
        {isBoss ? t('dashboard.active.toFinal') : t('dashboard.active.continue')}
      </button>
      {onChooseOther && (
        <button
          type="button"
          // Подсветка после знакомства: если первую тренировку бросили, вход в выбор тренировки — здесь.
          data-tour="training"
          onClick={onChooseOther}
          // dh-active-link: в стопке компьютера поля снимаются — текст ровно по левому краю кнопки (hero.css).
          className="dh-active-link min-h-11 px-2 text-[14px] font-medium text-muted underline decoration-muted/40 underline-offset-4 transition-colors hover:text-fg hover:decoration-fg"
        >
          {t('training.newSession')}
        </button>
      )}
    </>
  );

  if (phone) {
    return (
      <div className="flex flex-col p-4">
        {body}
        {/* Между кнопкой и областью ссылки (44 px) — 8 px, а не вплотную (приёмка A1-10). */}
        <div className="mt-4 flex flex-col items-center gap-2">{actions}</div>
      </div>
    );
  }

  // Узкая колонка (окна 901–~1100 px с развёрнутым меню): рядом с кнопкой заголовку оставалось ~200 px,
  // «Осталось 6 карточек · 2 к повторению» вставал в три строки, и line-clamp срезал число к повторению.
  // Там кнопки уходят под текст — контейнерным запросом по ширине карточки (hero.css).
  return (
    <div className="dh-active">
      <div className="dh-hero dh-active-card flex items-center gap-6 px-8">
        <div className="dh-active-body min-w-0 flex-1">{body}</div>
        <div className="dh-active-actions flex flex-none flex-col items-center gap-2">{actions}</div>
      </div>
    </div>
  );
}
